import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, OnDestroy, ViewChild, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Navbar } from './components/navbar/navbar';
import { LoadingSpinner } from './components/loading-spinner/loading-spinner';
import { I18nService } from './core/i18n.service';
import { ApiService } from './core/api.service';
import { environment } from '../environments/environment';

const STORAGE_NOTICE_ACKNOWLEDGED_KEY = 'phdcompass.storageNoticeAcknowledged';
const VISIT_RECORDED_SESSION_KEY = 'phdcompass.visitRecordedSession';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [Navbar, RouterOutlet, RouterLink, LoadingSpinner],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements AfterViewInit, OnDestroy {
  readonly appVersion = environment.version;
  readonly isRouteLoading = signal(false);
  readonly isStorageNoticeVisible = signal(localStorage.getItem(STORAGE_NOTICE_ACKNOWLEDGED_KEY) !== 'true');
  readonly isPageBottom = signal(false);
  readonly isBackToTopVisible = signal(false);
  @ViewChild('appFooter') private appFooter?: ElementRef<HTMLElement>;
  @HostBinding('style.--app-footer-height') footerHeight = '0px';
  private readonly routerEventsSubscription;
  private footerMeasureFrame = 0;

  constructor(private router: Router, public i18n: I18nService, private api: ApiService) {
    this.routerEventsSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isRouteLoading.set(true);
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.isRouteLoading.set(false);
        setTimeout(() => this.updateViewportState());
      }
    });
    setTimeout(() => this.updateViewportState());
    void this.recordVisitOncePerSession();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateViewportState());
  }

  ngOnDestroy(): void {
    this.routerEventsSubscription.unsubscribe();
    if (this.footerMeasureFrame) cancelAnimationFrame(this.footerMeasureFrame);
  }

  acknowledgeStorageNotice(): void {
    localStorage.setItem(STORAGE_NOTICE_ACKNOWLEDGED_KEY, 'true');
    this.isStorageNoticeVisible.set(false);
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  updateViewportState(): void {
    const documentElement = document.documentElement;
    const scrollTop = window.scrollY || documentElement.scrollTop || 0;
    const viewportHeight = window.innerHeight || documentElement.clientHeight;
    const documentHeight = documentElement.scrollHeight;
    this.isBackToTopVisible.set(scrollTop > 80);
    this.isPageBottom.set(scrollTop + viewportHeight >= documentHeight - 8);
    if (this.footerMeasureFrame) cancelAnimationFrame(this.footerMeasureFrame);
    this.footerMeasureFrame = requestAnimationFrame(() => {
      this.footerHeight = `${Math.ceil(this.appFooter?.nativeElement.getBoundingClientRect().height ?? 0)}px`;
      this.footerMeasureFrame = 0;
    });
  }

  private async recordVisitOncePerSession(): Promise<void> {
    if (sessionStorage.getItem(VISIT_RECORDED_SESSION_KEY) === 'true') return;
    sessionStorage.setItem(VISIT_RECORDED_SESSION_KEY, 'true');
    try {
      await this.api.recordVisit();
    } catch {
      sessionStorage.removeItem(VISIT_RECORDED_SESSION_KEY);
    }
  }
}
